import { Input, EventEmitter, Output, Component } from "@angular/core";
import { FormGroup, FormControl, AbstractControl } from "@angular/forms";
import { WorkflowData, ApplicationState, ApplicationSteps, WorkflowNote } from "../../models/applications.models";
import { CollapsibleComponent } from "../../../core/components/collapsible/collapsible.component";
import { NoteType } from "../../../common/models/note-type.enum";
import { AuthService } from "../../../services/auth.service";

@Component({
    selector: 'app-section-base',
    templateUrl: './application-section-base.component.html',
    styleUrls: ['../../../core/components/collapsible/collapsible.component.scss', './application-section-base.component.scss'],
    standalone: false
})
export class ApplicationSectionBaseComponent extends CollapsibleComponent {

    @Input() activeStep: ApplicationSteps; 

    @Input() alwaysShowNotes: boolean = false;

    @Input() application: WorkflowData;

    get applicationState(): ApplicationState {
        if (this.application == null) return ApplicationState.Start;
        return this.application.applicationState;
    }

    coordinateHelpText: string = "Use NAD83 Latitude & Longitude coordinates.  Latitude & longitude coordinates will be rounded to six decimal places.";

    @Input() form: FormGroup = new FormGroup({});

    get isReviewState(): boolean {
        if (this.application == null) return false;
        return this.reviewStates.includes(this.application.applicationState);
    }

    @Output() notesRequested: EventEmitter<string> = new EventEmitter<string>();

    @Output() openChanged: EventEmitter<ApplicationSteps> = new EventEmitter<ApplicationSteps>();

    reviewStates: ApplicationState[] = [
        ApplicationState.VerifyCompletion,
        ApplicationState.InitialReview,
        ApplicationState.InitialApproval,
        ApplicationState.FinalApproval
    ];

    states = ApplicationState;


    constructor(private auth: AuthService) { super(); }

    fieldHasNotes(fieldName: string): boolean {
        return this.alwaysShowNotes || this.getFilteredNotes(fieldName).length > 0;
    }

    findMatchingControl(formControlName: string): FormControl {
        let field: AbstractControl = null;
        let parts = formControlName.split('|');
        parts.forEach(n => {
            if (n.startsWith('|')) n = n.substring(1, n.length - 1);
            else if (n.endsWith('|')) n = n.substring(0, n.length - 2);
        })
        switch (parts.length) {
            case 1:
                field = this.form.get(parts[0]);
                break;
            case 2:
                field = this.form.get(parts[0]).get(parts[1]);
                break;
            case 3:
                field = this.form.get(parts[0]).get(parts[1]).get(parts[2]);
                break;
        }
        return <FormControl>field;
    }


    sortNotes(notes: WorkflowNote[]): WorkflowNote[] {
        let parented = notes.filter(f => f.parentNoteId);
        let unparented = notes.filter(f => !f.parentNoteId);
        parented.forEach(note => {
            const parent = unparented.find(n => n.id == note.parentNoteId);
            if (parent) {
                if (parent.childNotes.includes(note) == false) {
                    parent.childNotes.push(note);
                }
            }
        });

        return unparented;
    }

    getFilteredNotes(fieldName: string): WorkflowNote[] {
        if (this.application == null) return [];
        if (this.application.workflowNotes == null) return [];
        if (this.application.workflowFieldDatas == null) return [];
        let field = this.application.workflowFieldDatas.find(f => f.formControlName == fieldName);
        if (field == null) return [];

        let selectedNotes = (this.application.workflowNotes == null)
            ? []
            : this.application.workflowNotes;

        let unparented = this.sortNotes(selectedNotes);

        if (this.auth.User.isInternal) return unparented.filter(f => f.fieldId == field.id).filter(n => n.noteTypeId != NoteType.Administrative || n.createdById == this.auth.User.id);
        else return unparented.filter(f => f.fieldId == field.id).filter(n => n.noteTypeId == NoteType.General);
    }

    handleNoteRequested(field: string): void {
        this.notesRequested.emit(field);
    }

    roundCoordinate(ctrlName: string): void {
        let ctrl = this.findMatchingControl(ctrlName);
        if (ctrl == null) return;
        let value = ctrl.value;
        if (value == null || isNaN(value)) return;
        let numValue = parseFloat(value);
        let fixedValue = numValue.toFixed(6);
        ctrl.setValue(parseFloat(fixedValue));
    }

}